import React, { FC } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type ResultsTableProps = {
    values: string[][];
};

export const TableDoubles: FC<ResultsTableProps> = ({ values }) => {
    const [headers, ...rows] = values;

    const countryColIndex = headers.findIndex((h) => h.toLowerCase() === 'country');
    const qColIndex = headers.findIndex((h) => h.toLowerCase() === 'q');

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';
    const isQualified = (value?: string) => value === 'Q';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const teams = [];
    for (let i = 0; i < rows.length; i += 2) {
        teams.push([rows[i], rows[i + 1]]);
    }

    // =========================================================
    // 📱 MOBILE VIEW
    // =========================================================
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2, marginBottom: 2 }}>
                {teams.map(([rider1, rider2], index) => {
                    if (!rider1) return null;

                    const rank = rider1[0]; // usually same for both
                    const country1 = rider1[countryColIndex];
                    const country2 = rider2[countryColIndex];
                    const lastValue = rider1[rider1.length - 1];
                    const bib = rider1[1];

                    return (
                        <Paper
                            key={index}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                }}
                            >
                                {/* Rank */}
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {rank === 'DNF' ? '' : '# '}
                                    {rank}
                                    <Typography
                                        sx={{
                                            fontSize: '0.9rem',
                                            lineHeight: 1.1,
                                            pt: 3,
                                        }}
                                    >
                                        {bib}
                                    </Typography>
                                </Typography>

                                {/* Names */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                    }}
                                >
                                    {/* Rider 1 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            p: 0.5,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                                {rider1[2]}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    pl: 1.5,
                                                    color: 'text.secondary',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {rider1[3]}
                                            </Typography>
                                        </Box>

                                        <img
                                            src={getFlagUrl(country1)}
                                            alt={country1}
                                            className="flag-icon"
                                            style={{ width: '24px', height: '17px' }}
                                        />
                                    </Box>

                                    {/* Rider 2 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            p: 0.5,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                                {rider2[2]}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    pl: 1.5,
                                                    color: 'text.secondary',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {rider2[3]}
                                            </Typography>
                                        </Box>

                                        <img
                                            src={getFlagUrl(country2)}
                                            alt={country2}
                                            className="flag-icon"
                                            style={{ width: '24px', height: '17px' }}
                                        />
                                    </Box>
                                </Box>

                                {/* Points */}
                                <Box
                                    sx={{
                                        minWidth: 55,
                                        textAlign: 'right',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        {lastValue} {lastValue === 'DNF' ? '' : 'pts'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                overflowX: 'auto',
                borderRadius: 2,
                boxShadow: 3,
                marginTop: 2,
                marginBottom: 2,
            }}
        >
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <TableCell
                                key={index}
                                align={'center'}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'grey.900',
                                    color: 'common.white',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, rowIndex) => {
                        const isGroupStart = rowIndex % 2 === 0; // Rnk column
                        const nextRow = rows[rowIndex + 1];
                        const isDouble = isGroupStart && nextRow && nextRow[0] === '';

                        return (
                            <TableRow
                                key={rowIndex}
                                sx={{
                                    [`& .${tableCellClasses.root}`]: {
                                        borderBottom: (theme) =>
                                            rowIndex % 2 === 0 || rowIndex === rows.length - 1
                                                ? ''
                                                : theme.palette.mode === 'light'
                                                  ? 'solid black 2px'
                                                  : 'solid white 2px',
                                    },
                                }}
                            >
                                {headers.map((_, cellIndex) => {
                                    const cellValue = row[cellIndex];
                                    const abandoned = isAbandoned(cellValue);
                                    const qualified = cellIndex === qColIndex && isQualified(cellValue);

                                    const isMergeColumn = [0, 1, 5].includes(cellIndex); // Rnk, Bib, Points

                                    // 👉 MERGED CELLS
                                    if (isMergeColumn) {
                                        // skip rendering in second row
                                        if (!isGroupStart) return null;

                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                rowSpan={isDouble ? 2 : 1}
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: cellIndex === 0 ? 600 : 400,
                                                    borderBottom: (theme) =>
                                                        rowIndex === rows.length - 2
                                                            ? 'none'
                                                            : theme.palette.mode === 'light'
                                                              ? 'solid black 2px'
                                                              : 'solid white 2px',
                                                }}
                                            >
                                                {cellValue ?? '—'}
                                            </TableCell>
                                        );
                                    }

                                    // 👉 QUALIFIED BADGE (unchanged)
                                    if (qualified) {
                                        return (
                                            <TableCell key={cellIndex} align="center">
                                                <span className="qualified-badge">Q</span>
                                            </TableCell>
                                        );
                                    }

                                    // 👉 COUNTRY WITH FLAG (unchanged)
                                    if (cellIndex === countryColIndex && cellValue) {
                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                sx={{
                                                    border: 'none',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        fontWeight: abandoned ? 600 : 400,
                                                    }}
                                                >
                                                    <img
                                                        src={getFlagUrl(cellValue)}
                                                        alt={cellValue}
                                                        className="flag-icon"
                                                    />
                                                </Box>
                                            </TableCell>
                                        );
                                    }

                                    // 👉 DEFAULT CELL (unchanged)
                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            align="center"
                                            sx={{
                                                fontWeight: abandoned || cellIndex === 0 ? 600 : 400,
                                                border: 'none',
                                            }}
                                        >
                                            {cellValue ?? '—'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
